import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppProvider, Frame } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import App from './App';
import '@shopify/polaris/build/esm/styles.css';
import '@reach/settings-workspace/styles.css';
import './index.css';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN, environment: import.meta.env.MODE });
}

const SHOPIFY_TOKEN_STORAGE_KEY = 'reach_shopify_session_token';
const WORDPRESS_TOKEN_STORAGE_KEY = 'reach_wordpress_session_token';
const PREVIEW_BEARER_STORAGE_KEY = 'reach_preview_bearer_token';
const _originalFetch = window.fetch.bind(window);
const urlParams = new URLSearchParams(window.location.search);
const shopifyTokenFromUrl = urlParams.get('shopify_session_token');
const wordpressTokenFromUrl = urlParams.get('wp_session_token');
const previewHandoffToken = urlParams.get('preview_session_token');
let previewReadyPromise: Promise<void> | null = null;
let previewError: string | null = null;

if (shopifyTokenFromUrl) {
  window.sessionStorage.setItem(SHOPIFY_TOKEN_STORAGE_KEY, shopifyTokenFromUrl);
}

if (wordpressTokenFromUrl) {
  window.sessionStorage.setItem(WORDPRESS_TOKEN_STORAGE_KEY, wordpressTokenFromUrl);
}

if (previewHandoffToken) {
  urlParams.delete('preview_session_token');
  const cleanUrl = [window.location.pathname, urlParams.toString() ? `?${urlParams.toString()}` : ''].join('');
  window.history.replaceState({}, '', cleanUrl);

  previewReadyPromise = _originalFetch('/api/preview/redeem', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: previewHandoffToken }),
  }).then(async (res) => {
    if (!res.ok) {
      previewError = res.status === 401
        ? 'Preview link has expired. Please generate a new one from the console.'
        : `Preview token redemption failed (${res.status}).`;
      throw new Error(previewError);
    }
    const data = await res.json() as { bearerToken?: string };
    if (data.bearerToken) {
      window.sessionStorage.setItem(PREVIEW_BEARER_STORAGE_KEY, data.bearerToken);
    }
  }).catch((err) => {
    if (!previewError) previewError = 'Preview link expired or invalid.';
    console.error('[Reach Preview] Token redemption failed:', err);
  });

  (window as any).__reachPreviewReady = previewReadyPromise;
  (window as any).__reachPreviewError = () => previewError;
}

window.fetch = (input, init = {}) => {
  const url = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : (input as Request).url;

  if (url.includes('/api/merchant')) {
    const withMerchantAuth = () => {
      const headers = new Headers((init as RequestInit).headers);
      const previewBearerToken = window.sessionStorage.getItem(PREVIEW_BEARER_STORAGE_KEY);
      const shopifySessionToken = window.sessionStorage.getItem(SHOPIFY_TOKEN_STORAGE_KEY);
      const wordpressSessionToken = window.sessionStorage.getItem(WORDPRESS_TOKEN_STORAGE_KEY);

      if (previewBearerToken) {
        headers.set('Authorization', `Bearer ${previewBearerToken}`);
      } else if (shopifySessionToken) {
        headers.set('Authorization', `Bearer ${shopifySessionToken}`);
      } else if (wordpressSessionToken) {
        headers.set('Authorization', `Bearer ${wordpressSessionToken}`);
      }

      return _originalFetch(input, { ...init, headers });
    };

    const request = previewReadyPromise ? previewReadyPromise.then(withMerchantAuth) : withMerchantAuth();
    return request.then((res) => {
      if (res.status === 451) {
        window.dispatchEvent(new CustomEvent('reach:legal-consent-required'));
      }
      return res;
    });
  }

  return _originalFetch(input, init).then((res) => {
    if (res.status === 451) {
      window.dispatchEvent(new CustomEvent('reach:legal-consent-required'));
    }
    return res;
  });
};

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1 } } });
const DASHBOARD_THEME_STORAGE_KEY = 'reach_dashboard_theme';
const LOCKED_DASHBOARD_THEME = 'ocean';
document.documentElement.setAttribute('data-theme', LOCKED_DASHBOARD_THEME);
localStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, LOCKED_DASHBOARD_THEME);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppProvider i18n={enTranslations}>
      <Frame>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
              <App />
            </Sentry.ErrorBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </Frame>
    </AppProvider>
  </React.StrictMode>
);
